import type { AuthUser } from '@technic/contracts';

/**
 * Отпечаток доступа учётки — «изменилось ли то, из-за чего сервер может ответить иначе»
 * (ADR 0160, Р15).
 *
 * Кэш запросов набирается при том доступе, какой был у человека в момент запроса, и ключи его не
 * содержат: `staleTime` справочников доходит до минут, а карточка заявки после ADR 0160 приходит
 * РАЗНОЙ в зависимости от аудитории. Поэтому смена доступа обязана выбрасывать кэш так же, как его
 * выбрасывает вход под другой учёткой.
 *
 * ПОЧЕМУ НЕ ОДНИ `permissions`. Права — половина ответа. Вторая половина — область: у той же роли с
 * тем же набором прав сменили площадку или отдел, и в кэше остались строки, которых человек больше
 * не видит. Список сервер пересчитает, а вот открытая карточка из кэша показывалась бы до
 * протухания.
 *
 * ПОЧЕМУ НЕ `authVersion`. Версия растёт и от смены пароля, и от любой правки учётки, к доступу
 * отношения не имеющей: чистить кэш на каждое продление сессии значило бы гонять портал за
 * справочниками впустую.
 *
 * СОРТИРОВКА КОПИЙ ОБЯЗАТЕЛЬНА. Сервер собирает эти списки запросами, и порядок в них не
 * гарантирован ничем: без сортировки та же самая область, пришедшая в другом порядке, читалась бы
 * как смена доступа и выбрасывала кэш на ровном месте. Копия (`[...]`) — потому что `sort` правит
 * массив на месте, а это поле живой учётки, которое читают и рисуют.
 */
export function accessFingerprint(user: AuthUser | null): string {
  if (!user) return '';
  /*
   * Во время совместимого выката новая клиентская сборка может на один bootstrap получить ответ
   * старого API без только что добавленного списка. Отсутствующий список здесь равен пустому:
   * доступ он не добавляет, а падение до следующего refresh заперло бы весь портал. Тип AuthUser
   * остаётся строгим для нового кода; optional нужен ровно на этой границе версий.
   */
  const list = (values: readonly string[] | undefined): string =>
    [...(values ?? [])].sort().join(',');
  return [
    user.role ?? '',
    user.counterpartyType ?? '',
    user.counterpartyId ?? '',
    list(user.permissions),
    list(user.grantCodes),
    list(user.constructionObjectIds),
    list(user.departmentIds),
    list(user.departmentObjectIds),
  ].join('|');
}
